import { memo, useMemo } from 'react';
import type { CellContext } from '@tanstack/react-table';
import { isUndefined } from 'utils/typeChecks';
import { styled, Typography } from '@mui/material';
import i18n from 'i18next';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../../utils/styles.ts';
import { getIcon } from '../../../../utils/common.ts';
import type { FormItemUiDefinition } from '../../../../types/formItem.ts';
import { ItemContainer } from '../../ItemContainer.tsx';
import { TABLE_CELL_TYPE } from '../../../../types/enums.ts';
import { isEmailType, isPhoneNumberType, stopPropagation } from '../../../../utils/component.ts';
import type { FormItemDefinition } from '../../../../types/formItem.ts';
import { formatFormItemNumberValue } from '../../../../utils/helios/numberFormatting';
import { useLocaleCodeIsoNorma } from '../../../../utils/hooks';

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isClickable' && prop !== 'hasValue',
})<{
  isClickable?: boolean;
  hasValue?: boolean;
}>(({ isClickable, hasValue, theme }) => ({
  transition: 'all',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: isClickable ? 'underline' : 'none',
    cursor: isClickable ? 'pointer' : 'default',
  },
  color: !hasValue ? `${theme.palette.text.secondary}` : 'inherit',
  fontStyle: !hasValue ? 'italic' : 'normal',
}));

const contactComponentProps = (isClickable: boolean) => {
  return {
    className: `transition-all ${isClickable ? 'hover:underline cursor-pointer' : ''}`,
    onClick: stopPropagation,
    style: { color: 'inherit', textDecoration: 'none' },
  };
};

interface Props<T, P> {
  type: TABLE_CELL_TYPE;
  originalType?: string;
  value?: string;
  row?: CellContext<T, P>;
  onClick?: (row: T, cell: keyof P) => unknown;
  isClickable?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
  formItemDefinition?: FormItemDefinition;
  wrap?: boolean;
}

const TextBase = <T, P>({
  type,
  originalType = '',
  value,
  row,
  onClick,
  isClickable = false,
  wrap = false,
  formItemUiDefinition,
  formItemDefinition,
}: Props<T, P>) => {
  const locale = useLocaleCodeIsoNorma();
  const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);
  const containerStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
  const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition.icon) : null;

  const handleRowClick = () => {
    if (onClick) {
      onClick(row?.row?.original as T, row?.column?.id as keyof P);
    }
  };

  const showNotFilled = type !== TABLE_CELL_TYPE.EMAIL;

  const hasValue = !isUndefined(value) && String(value).length > 0;

  const textValue = useMemo(() => {
    if (!hasValue && showNotFilled) {
      return '';
    }

    const columnId = row?.column?.id as string;

    if (isPhoneNumberType(type, originalType, columnId)) {
      return (
        <a href={`tel:${value}`} {...contactComponentProps(isClickable)}>
          {value}
        </a>
      );
    }

    if (isEmailType(type, originalType, columnId)) {
      return (
        <a href={`mailto:${value}`} {...contactComponentProps(isClickable)}>
          {value}
        </a>
      );
    }

    if (type === TABLE_CELL_TYPE.NUMBER || originalType === 'Number') {
      const numberDefinition = formItemDefinition?.Number ?? formItemDefinition?.number;
      if (numberDefinition) {
        const formattedValue = formatFormItemNumberValue(value, locale, numberDefinition);
        if (formattedValue !== null) {
          return `${formattedValue}${numberDefinition.unit ? ` ${numberDefinition.unit}` : ''}`;
        }
      } else {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          return new Intl.NumberFormat(i18n.language).format(numericValue);
        }
      }
    }

    return value;
  }, [formItemDefinition, hasValue, isClickable, locale, originalType, row?.column?.id, showNotFilled, type, value]);

  return (
    <ItemContainer styles={containerStyles}>
      {Icon && <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />}
      <StyledTypography variant="body1" isClickable={isClickable} hasValue={hasValue} noWrap={!wrap} onClick={handleRowClick} sx={textStyles}>
        {textValue}
      </StyledTypography>
    </ItemContainer>
  );
};

export const Text = memo(TextBase) as typeof TextBase;
